import { useState } from 'react';
import { ChevronLeftIcon, ChevronDownIcon, SearchIcon } from '../components/Icons';

const FAQ_DATA = [
  {
    category: 'Product & Sizing',
    qas: [
      {
        q: 'How do I choose the correct size for a gown?',
        a: 'We provide a detailed Size Guide on each product details page. You can match your bust, waist, and hip measurements against our UK/US sizes to find the best fit.',
      },
      {
        q: 'Can I request customizations for a gown or accessory?',
        a: 'We sell gowns and accessories in standard sizes. For custom adjustments, we recommend taking the item to a professional tailor after purchase.',
      },
    ],
  },
  {
    category: 'Shipping & Delivery',
    qas: [
      {
        q: 'How long does it take for my order to arrive?',
        a: 'Standard orders take 24 to 48 hours for delivery nationwide. Tracking information is sent via email and SMS as soon as your package is dispatched.',
      },
      {
        q: 'How are gowns packaged for transit?',
        a: 'All gowns are shipped in custom, water-resistant protective bridal boxes to keep them safe, clean, and in pristine condition during transit.',
      },
      {
        q: 'Do you ship internationally?',
        a: 'Yes, we offer worldwide shipping. Delivery rates and times are calculated automatically at checkout based on your country.',
      },
    ],
  },
  {
    category: 'Order & Payment',
    qas: [
      {
        q: 'What payment methods do you accept?',
        a: 'We accept payments through Monnify. You can pay securely with your credit/debit card, bank transfer, or other Monnify payment channels.',
      },
      {
        q: 'Can I cancel or change my order?',
        a: 'Orders can be cancelled or updated within 2 hours of payment. Please contact our support team immediately if you need to make changes.',
      },
    ],
  },
];

export default function FAQPage({ onNavigate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [openQIndex, setOpenQIndex] = useState(null); // format: categoryIndex-qaIndex

  const filteredFAQs = FAQ_DATA.map((cat, catIdx) => {
    const matchedQas = cat.qas.filter(
      (qa) =>
        qa.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
        qa.a.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return {
      ...cat,
      qas: matchedQas,
      originalIdx: catIdx,
    };
  }).filter(
    (cat) =>
      (activeCategory === 'All' || cat.category === activeCategory) &&
      cat.qas.length > 0
  );

  const categories = ['All', ...FAQ_DATA.map((c) => c.category)];

  const toggleQA = (key) => {
    setOpenQIndex(openQIndex === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-[#FAF3F0]">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 lg:px-8 pt-5 pb-4 bg-[#FAF3F0]">
        <button onClick={() => onNavigate('account')} className="p-1.5 -ml-1.5 text-[#F47B20]">
          <ChevronLeftIcon className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-bold text-[#1A1A2E]">FAQs</h1>
      </div>

      <div className="px-4 lg:px-8 pb-28 lg:pb-12 max-w-3xl">
        <h2 className="text-xl font-black text-[#1A1A2E] mb-1">Frequently Asked Questions</h2>
        <p className="text-gray-400 text-sm mb-6">Find answers to common questions about shipping, products, and payments.</p>

        {/* Search */}
        <div className="flex items-center gap-2 bg-white rounded-2xl px-4 py-3.5 border border-gray-200 shadow-sm mb-6">
          <SearchIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search FAQs (e.g. shipping, payments)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none text-gray-700 placeholder-gray-400"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-gray-400 hover:text-gray-600 text-xs font-bold">
              ✕
            </button>
          )}
        </div>

        {/* Categories Pills */}
        <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => {
                setActiveCategory(cat);
                setOpenQIndex(null);
              }}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors border ${
                activeCategory === cat
                  ? 'bg-[#1A1A2E] text-white border-[#1A1A2E]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ List */}
        <div className="space-y-6 mt-4">
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((cat, catIdx) => (
              <div key={cat.category}>
                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">
                  {cat.category}
                </h3>
                <div className="space-y-3">
                  {cat.qas.map((qa, qaIdx) => {
                    const key = `${cat.originalIdx}-${qaIdx}`;
                    const isOpen = openQIndex === key;
                    return (
                      <div
                        key={qa.q}
                        className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm transition-all"
                      >
                        <button
                          onClick={() => toggleQA(key)}
                          className="w-full flex items-center justify-between p-4 text-left font-bold text-[#1A1A2E] hover:bg-gray-50 transition-colors gap-4"
                        >
                          <span className="text-sm leading-snug">{qa.q}</span>
                          <ChevronDownIcon
                            className={`w-4 h-4 text-[#F47B20] transition-transform duration-300 shrink-0 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-4 text-sm text-gray-500 leading-relaxed border-t border-gray-50 pt-3">
                            {qa.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-3xl p-12 text-center border border-gray-100 shadow-sm">
              <SearchIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">No matches found for "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
